import type { ReactNode } from 'react';
import type { PlotScale } from '@paige/ui';
import {
  barBox,
  baseOf,
  bucketWord,
  labelled,
  linePoints,
  midOf,
  scaleFor,
  type ChartSeries,
  type Plot,
} from './period-chart-geometry';
import type { Period } from './period';

/** The tick values whose formatted text is new: a tiny scale never reads "$0 $0 $1 $1". */
function distinct(scale: PlotScale, format: (n: number) => string): { at: number; text: string }[] {
  const seen = new Set<string>();
  return scale.ticks
    .map((at) => ({ at, text: format(at) }))
    .filter((t) => !seen.has(t.text) && Boolean(seen.add(t.text)));
}

/** The second unit's axis: its values down the right edge, and each axis named above its values. */
function RightAxis({ plot, series }: { plot: Plot; series: readonly ChartSeries[] }): ReactNode {
  const second = series.find((_, i) => plot.onRight[i]);
  const right = plot.right;
  if (!right || !second) return null;
  const end = plot.width - plot.pad.right;
  return (
    <>
      <text className="ad-pchart__axis-name" x={0} y={11}>
        {series[0]?.label}
      </text>
      <text className="ad-pchart__axis-name" x={plot.width} y={11} textAnchor="end">
        {second.label}
      </text>
      {distinct(right, second.format).map((t) => (
        <text key={t.at} data-right-tick={t.at} x={end + 6} y={right.y(t.at) + 4}>
          {t.text}
        </text>
      ))}
    </>
  );
}

/** The gridlines and the left axis's values, and the right axis's when a second unit is drawn. */
export function PlotAxes({
  plot,
  series,
}: {
  plot: Plot;
  series: readonly ChartSeries[];
}): ReactNode {
  const first = series[0];
  if (!first) return null;
  return (
    <g className="ad-pchart__axes">
      {distinct(plot.left, first.format).map((t) => (
        <g key={t.at} data-tick={t.at}>
          <line
            className="pui-chart__grid"
            x1={plot.pad.left}
            x2={plot.width - plot.pad.right}
            y1={plot.left.y(t.at)}
            y2={plot.left.y(t.at)}
          />
          <text x={plot.pad.left - 6} y={plot.left.y(t.at) + 4} textAnchor="end">
            {t.text}
          </text>
        </g>
      ))}
      <RightAxis plot={plot} series={series} />
    </g>
  );
}

/** The dates under the plot, thinned to what fits. */
export function BucketLabels({
  plot,
  buckets,
}: {
  plot: Plot;
  buckets: readonly Period[];
}): ReactNode {
  return (
    <g>
      {labelled(plot).map((i) => {
        const bucket = buckets[i];
        return bucket ? (
          <text key={bucket.from} x={midOf(plot, i)} y={plot.height - 6} textAnchor="middle">
            {bucketWord(bucket)}
          </text>
        ) : null;
      })}
    </g>
  );
}

/** One series: bars standing on the baseline, or a line through the bucket middles. */
function SeriesMarks({
  plot,
  series,
  index,
  bars,
}: {
  plot: Plot;
  series: ChartSeries;
  index: number;
  /** This series' place among the bar series, and how many there are. */
  bars: { slot: number; slots: number };
}): ReactNode {
  const scale = scaleFor(plot, index);
  const tone = `ad-pchart__s--${series.tone ?? (index === 0 ? 'accent' : 'muted')}`;
  if (series.kind === 'line') {
    return (
      <polyline
        className={`ad-pchart__line ${tone}`}
        data-series={series.key}
        points={linePoints(plot, scale, series.values)}
      />
    );
  }
  return (
    <g className={`ad-pchart__bars ${tone}`} data-series={series.key}>
      {series.values.slice(0, plot.count).map((v, i) => {
        const box = barBox(plot, scale, { i, ...bars }, v);
        return <rect key={i} {...box} rx={Math.min(3, box.width / 2)} data-value={v} />;
      })}
    </g>
  );
}

export interface PlotMarksProps {
  plot: Plot;
  series: readonly ChartSeries[];
  previous?: readonly number[] | undefined;
  /** The bucket under the pointer or the keyboard. */
  active: number | null;
  /** The bucket the page is narrowed to. */
  picked: number | null;
}

/** A bucket's full-height band: the one under the pointer, or the one picked. */
function Band({ plot, i, kind }: { plot: Plot; i: number; kind: 'active' | 'picked' }): ReactNode {
  return (
    <rect
      className={`ad-pchart__band ad-pchart__band--${kind}`}
      x={plot.pad.left + i * plot.step}
      y={plot.pad.top}
      width={plot.step}
      height={plot.innerH}
    />
  );
}

/** Everything drawn in the plot: the bands, the bars, the previous period's faint line, the lines. */
export function PlotMarks({ plot, series, previous, active, picked }: PlotMarksProps): ReactNode {
  const barKeys = series.filter((s) => s.kind === 'bars').map((s) => s.key);
  const slots = Math.max(1, barKeys.length);
  return (
    <g>
      {picked !== null ? <Band plot={plot} i={picked} kind="picked" /> : null}
      {active !== null ? <Band plot={plot} i={active} kind="active" /> : null}
      <line
        className="ad-pchart__base"
        x1={plot.pad.left}
        x2={plot.width - plot.pad.right}
        y1={baseOf(plot)}
        y2={baseOf(plot)}
      />
      {series.map((s, index) => (
        <SeriesMarks
          key={s.key}
          plot={plot}
          series={s}
          index={index}
          bars={{ slot: Math.max(0, barKeys.indexOf(s.key)), slots }}
        />
      ))}
      {previous ? (
        <polyline
          className="ad-pchart__previous"
          data-series="previous"
          points={linePoints(plot, plot.left, previous)}
        />
      ) : null}
    </g>
  );
}
